import { Card, Stack, useMediaQuery, useTheme } from '@mui/material'
import { ru } from 'date-fns/locale'
import { FC, useEffect, useMemo, useState } from 'react'
import { APP_TOP_INDENT, APP_TOP_INDENT_MOBILE } from '../../../constants/app.indents'
import { EventsSidebar } from './components/EventsSidebar'
import { Header } from './components/Header'
import { WeekCalendar } from './components/WeekCalendar'

import {
    addDays,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    getWeek,
    isBefore,
    isSameDay,
    startOfDay,
    startOfMonth,
    startOfWeek,
} from 'date-fns'
import { useSearchParams } from 'react-router-dom'
import {
    useMeetingListApiV1CollaborationAppMeetingGet,
    useReplyScheduleCalendarApiV1ReplyScheduleCalendarGet,
} from '../../../api/gen/base'
import { useLocalStorage } from '../../../hooks/useLocalStorage'
import { CalendarData, CalendarEvent } from '../../../types/calendar'
import { MonthCalendar } from './components/MonthCalendar'

export const CalendarPage: FC = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const { breakpoints } = useTheme()

    const isMobile = useMediaQuery(breakpoints.down('md'))

    const currentMonth = searchParams.get('month')
        ? new Date(searchParams.get('month'))
        : new Date()
    const currentWeek = searchParams.get('week') ? +searchParams.get('week') : getWeek(currentMonth)
    const selectedDate = searchParams.get('selectedDate')
        ? new Date(searchParams.get('selectedDate'))
        : startOfDay(new Date())

    const [displayWeekCalendar, setDisplayWeekCalendar] = useLocalStorage(
        'displayWeekCalendar',
        !searchParams.has('calendar'),
    )

    const isWeekViewActive = displayWeekCalendar && !isMobile

    const [data, setData] = useState<CalendarData>({})

    const { startDate, endDate } = useMemo(() => {
        if (isWeekViewActive) {
            const startDate = startOfWeek(currentMonth, { weekStartsOn: 1 })
            const endDate = addDays(startDate, 6)

            return { startDate, endDate }
        }

        const firstDayOfMonth = startOfMonth(currentMonth)
        const lastDayOfMonth = endOfMonth(currentMonth)
        const startDate = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 })
        const endDate = endOfWeek(lastDayOfMonth, { weekStartsOn: 1 })

        return {
            startDate,
            endDate,
        }
    }, [currentMonth, isWeekViewActive])

    const { data: requestsData } = useReplyScheduleCalendarApiV1ReplyScheduleCalendarGet({
        limit: 1000,
        date_from: startDate.toISOString(),
        date_to: endDate.toISOString(),
    })
    const { data: meetingData } = useMeetingListApiV1CollaborationAppMeetingGet({
        limit: 1000,
        date_from: startDate.toISOString(),
        date_to: endDate.toISOString(),
    })

    const handleChangeCurrentMonth = (date: Date) => {
        searchParams.set('month', date.toISOString())
        setSearchParams(searchParams)
    }

    const handleChangeCurrentWeek = (week: number) => {
        searchParams.set('week', `${week}`)
        setSearchParams(searchParams)
    }

    const handleChangeSelectedDate = (date: Date) => {
        searchParams.set('selectedDate', date.toISOString())
        setSearchParams(searchParams)
    }

    const handleChangeDisplayWeekCalendar = (isActive: boolean) => {
        if (isActive) {
            searchParams.delete('calendar')
        } else {
            searchParams.set('calendar', `${!isActive}`)
        }
        setSearchParams(searchParams)
        setDisplayWeekCalendar(isActive)
    }

    useEffect(() => {
        handleChangeCurrentMonth(currentMonth)
        handleChangeCurrentWeek(currentWeek)
        handleChangeDisplayWeekCalendar(displayWeekCalendar)
        handleChangeSelectedDate(selectedDate)
    }, [])

    useEffect(() => {
        const days = eachDayOfInterval({ start: startDate, end: endDate })
        const rawData: CalendarData = days.reduce(
            (acc, day) => {
                const dayKey = day.toISOString()

                const requests =
                    requestsData?.objects?.filter((request) =>
                        isSameDay(new Date(request.timing.send_time), day),
                    ) ?? []
                const meetings =
                    meetingData?.objects?.filter((meeting) =>
                        isSameDay(new Date(meeting.start_at), day),
                    ) ?? []

                const dayValues = ([...requests, ...meetings] as CalendarEvent[]).sort((a, b) => {
                    const aDate = 'timing' in a ? a.timing.send_time : a.start_at
                    const bDate = 'timing' in b ? b.timing.send_time : b.start_at

                    return isBefore(aDate, bDate) ? -1 : 1
                })

                acc[dayKey] = dayValues

                return acc
            },
            { ...data } as CalendarData,
        )

        setData(rawData)
    }, [requestsData, meetingData])

    return (
        <Card
            sx={{
                height: '100%',
                maxHeight: `calc(100dvh - ${isMobile ? APP_TOP_INDENT_MOBILE : APP_TOP_INDENT}px)`,
                p: 0,
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
            }}>
            <Stack
                direction="column"
                sx={{
                    width: isMobile ? '100%' : 'calc(100% - 240px)',
                    p: 0,
                }}>
                <Header
                    displayWeekCalendar={isWeekViewActive}
                    currentMonth={currentMonth}
                    selectedDate={selectedDate}
                    onChangeMonth={handleChangeCurrentMonth}
                    onChangeWeek={handleChangeCurrentWeek}
                    onChangeCalendarView={handleChangeDisplayWeekCalendar}
                    onSelectDate={handleChangeSelectedDate}
                    locale={ru}
                />
                {isWeekViewActive ? (
                    <WeekCalendar
                        currentMonth={currentMonth}
                        currentWeek={currentWeek}
                        selectedDate={selectedDate}
                        onSelectDate={handleChangeSelectedDate}
                        locale={ru}
                        data={data}
                    />
                ) : (
                    <MonthCalendar
                        currentMonth={currentMonth}
                        currentWeek={currentWeek}
                        selectedDate={selectedDate}
                        onSelectDate={handleChangeSelectedDate}
                        locale={ru}
                        data={data}
                    />
                )}
            </Stack>
            <EventsSidebar selectedDate={selectedDate} locale={ru} data={data} />
        </Card>
    )
}
